import { type FunctionComponent } from "react";
import { CheckSquareOutlined, StarOutlined, TrophyOutlined } from "@ant-design/icons";
import { Spin, Tabs } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { PermissionKey } from "@fitness-app/data-models";

import NotFound from "~/components/NotFound/NotFound";
import { usePermission } from "~/hooks/usePermission";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { CHALLENGES_FEATURE_FLAG } from "~/modules/Challenges/challengesFeatureFlag";

// Single sidebar entry ("Zaangażowanie") grouping the challenges / habits / badges sections as
// sub-pages (tabs). URLs stay at the top level (/challenges, /habits, /badges) so every existing
// absolute link keeps working — this is only a shared shell: one feature-flag guard + a tab bar,
// with the section itself rendered through <Outlet/>.

const TABS = [
  { key: "/challenges", label: "Wyzwania", icon: <TrophyOutlined /> },
  { key: "/habits", label: "Nawyki i zadania", icon: <CheckSquareOutlined /> },
  { key: "/badges", label: "Odznaki", icon: <StarOutlined /> },
] as const;

const EngagementLayout: FunctionComponent = () => {
  const { enabled, isLoading } = usePostHogFeatureFlag(CHALLENGES_FEATURE_FLAG);
  const location = useLocation();
  const navigate = useNavigate();
  const canManageEngagement = usePermission(PermissionKey.ENGAGEMENT_MANAGE);
  // Odznaki gate'owane osobno — engagement.manage już ich nie implikuje.
  const canManageBadges = usePermission(PermissionKey.ENGAGEMENT_BADGES_MANAGE);
  const visibleTabs = TABS.filter((tab) =>
    tab.key === "/badges" ? canManageBadges : canManageEngagement,
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (!enabled) {
    return <NotFound />;
  }

  const activeKey =
    visibleTabs.find((tab) => location.pathname.startsWith(tab.key))?.key ?? visibleTabs[0]?.key;

  return (
    <div>
      <Tabs
        activeKey={activeKey}
        onChange={(key) => navigate(key)}
        items={visibleTabs.map((tab) => ({
          key: tab.key,
          // Use the item's own `icon` slot so antd spaces it from the label (embedding both in
          // one <span> glued the emoji/icon to the text).
          icon: tab.icon,
          label: tab.label,
        }))}
      />
      <Outlet />
    </div>
  );
};

export default EngagementLayout;
